import { useEffect } from "react";
import { Modal, Table, Alert, Button, Typography } from "antd";
import useCustomerReviewStore from "../../stores/useCustomerReviewStore";

// BUG-2370: the "please check before saving" popup for customer create / update,
// and the "was it really saved" result after a B2B save. Mounted once in App.js;
// customer.js talks to it through useCustomerReviewStore.
const changedStyle = { background: "#fff7e6" };
const sectionStyle = { background: "#f0f5ff", fontWeight: 600 };

const CustomerReviewHost = () => {
  const phase = useCustomerReviewStore((s) => s.phase);
  const review = useCustomerReviewStore((s) => s.review);
  const result = useCustomerReviewStore((s) => s.result);
  const answer = useCustomerReviewStore((s) => s.answer);
  const close = useCustomerReviewStore((s) => s.close);

  useEffect(() => {
    const { registerHost, unregisterHost, answer: answerNow } = useCustomerReviewStore.getState();
    registerHost();
    return () => {
      unregisterHost();
      // An unmount while the popup is open must not leave the save waiting forever.
      if (useCustomerReviewStore.getState()._resolve) answerNow(false);
    };
  }, []);

  const isCreate = review?.mode === "create";
  const colCount = isCreate ? 2 : 3;
  // A heading row spans the whole table; the other cells of that row are hidden.
  const sectionCell = (row) => (row.isSection ? { colSpan: 0 } : {});
  const columns = [
    {
      title: "Field",
      dataIndex: "label",
      key: "label",
      width: "28%",
      onCell: (row) => (row.isSection ? { colSpan: colCount, style: sectionStyle } : {}),
    },
    ...(isCreate ? [] : [{ title: "Before", dataIndex: "before", key: "before", width: "36%", onCell: sectionCell, render: (v) => v || "-" }]),
    {
      title: isCreate ? "Will be saved" : "After",
      dataIndex: "after",
      key: "after",
      onCell: sectionCell,
      render: (v, row) => <span style={row.changed && !isCreate ? { fontWeight: 600 } : undefined}>{v || "-"}</span>,
    },
  ];
  const dataSource = [];
  (review?.rows || []).forEach((row) => {
    const section = row.section || "";
    if (section && !dataSource.some((r) => r.isSection && r.label === section)) {
      dataSource.push({ key: `section:${section}`, label: section, isSection: true });
    }
    dataSource.push(row);
  });
  const typeText = review?.after?.b2b || "";

  return (
    <>
      <Modal
        open={phase === "review" && !!review}
        title={isCreate ? "Check the new customer before saving" : "Check the customer changes before saving"}
        closable={false}
        maskClosable={false}
        keyboard={false}
        width={760}
        zIndex={2000}
        footer={[
          <Button key="back" id="customer-review-back" onClick={() => answer(false)}>
            Go back and edit
          </Button>,
          <Button key="ok" id="customer-review-confirm" type="primary" onClick={() => answer(true)}>
            Confirm and save
          </Button>,
        ]}
      >
        {typeText && (
          <Typography.Paragraph id="customer-review-type" style={{ marginBottom: 8 }}>
            {isCreate ? "Saving as: " : "Will be saved as: "}
            <Typography.Text strong>{typeText}</Typography.Text>
          </Typography.Paragraph>
        )}
        {(review?.warnings || []).map((w) => (
          <Alert key={w} type="warning" showIcon message={w} style={{ marginBottom: 8 }} />
        ))}
        <Table
          size="small"
          pagination={false}
          rowKey="key"
          columns={columns}
          dataSource={dataSource}
          scroll={{ y: "55vh" }}
          onRow={(row) => (!row.isSection && row.changed && !isCreate ? { style: changedStyle } : {})}
        />
        {!isCreate && (
          <Typography.Text type="secondary" style={{ display: "block", marginTop: 8 }}>
            Highlighted rows will change.
          </Typography.Text>
        )}
      </Modal>
      <Modal
        open={phase === "result" && !!result}
        title={result?.status === "saved" ? "B2B customer saved" : result?.status === "not_saved" ? "B2B details were NOT saved" : "Could not confirm the save"}
        zIndex={2000}
        onCancel={close}
        footer={[
          <Button key="ok" id="customer-review-result-ok" type="primary" onClick={close}>
            OK
          </Button>,
        ]}
      >
        {result?.status === "saved" && <Alert type="success" showIcon message="The server holds this B2B customer with the billing and shipping addresses just saved." />}
        {result?.status === "not_saved" && (
          <Alert type="error" showIcon message={result.reason} description="Do not complete a B2B bill for this customer. Open the customer again, check the details and save again." />
        )}
        {result?.status === "unknown" && <Alert type="info" showIcon message={result.reason} />}
      </Modal>
    </>
  );
};

export default CustomerReviewHost;
